import { Code2, Github, type LucideIcon } from "lucide-react";

/**
 * Shared allowlist for repo sources (`github.com`, `codeberg.org`). The public
 * `/github.com/:owner/:repo` and `/codeberg.org/:owner/:repo` intake routes are
 * retired, but the source parsing/import path (submission wizard, Add App)
 * still uses this. Mirrors (but does not share code with — this app talks to
 * ato-api over HTTP, not via a shared package) the `GitHostConfig` allowlist in
 * ato-api's `services/git_hosts.ts`.
 *
 * Capability flags exist so a generalized page/component can check
 * `config.supportsX` instead of rendering a control that silently does
 * nothing for a host that doesn't support it.
 */
export type GitProvider = "github" | "codeberg";

export type GitHostConfig = {
  provider: GitProvider;
  host: string;
  label: string;
  /** `/v1/${apiPathSegment}/repos/...` on the store API. */
  apiPathSegment: string;
  webUrl(owner: string, repo: string): string;
  /** lucide-react has no Codeberg brand icon; Codeberg uses a generic "source code" glyph, not a logo reproduction. */
  icon: LucideIcon;
  /**
   * Whether the `ato` CLI can resolve a handle for this host today. The
   * CLI's handle parser only recognizes `github.com` — no "sign in with
   * <host>" / GitHub App private-repo flow, and no `ato run`/CLI command
   * snippets should be shown for a host it can't parse.
   */
  supportsAuthenticatedRepo: boolean;
  /**
   * How this host's README reaches the page.
   *
   * - `"html"` — the host serves pre-sanitized HTML with permissive CORS, so
   *   the browser fetches and embeds it directly. GitHub's
   *   `api.github.com/repos/:o/:r/readme` sends
   *   `access-control-allow-origin: *` (verified).
   * - `"markdown-proxy"` — the host sends NO CORS header (Codeberg), so the
   *   raw markdown comes through ato-api and is rendered locally by the
   *   escape-first renderer in `renderReadmeMarkdown.ts`.
   */
  readmeSource: "html" | "markdown-proxy";
};

export const GIT_HOST_CONFIGS: Record<GitProvider, GitHostConfig> = {
  github: {
    provider: "github",
    host: "github.com",
    label: "GitHub",
    apiPathSegment: "github",
    webUrl: (owner, repo) => `https://github.com/${owner}/${repo}`,
    icon: Github,
    supportsAuthenticatedRepo: true,
    readmeSource: "html",
  },
  codeberg: {
    provider: "codeberg",
    host: "codeberg.org",
    label: "Codeberg",
    apiPathSegment: "codeberg",
    webUrl: (owner, repo) => `https://codeberg.org/${owner}/${repo}`,
    icon: Code2,
    supportsAuthenticatedRepo: false,
    readmeSource: "markdown-proxy",
  },
};

export function getGitHostConfig(provider: GitProvider): GitHostConfig {
  return GIT_HOST_CONFIGS[provider];
}
